<template>
  <div class="shop" @click.self="$emit('close')">
    <div class="shop__panel" role="dialog" aria-modal="true" aria-labelledby="shop-title">
      <!-- Header -->
      <header class="shop__head">
        <div class="shop__title-wrap">
          <h2 id="shop-title" class="shop__title">Магазин поместо</h2>
          <p class="shop__subtitle">Детали для твоего персонажа и пространства</p>
        </div>
        <div class="shop__mobile-balance" aria-label="Баланс">
          <span aria-hidden="true">●</span>
          <strong>{{ coins }}</strong>
        </div>
        <button class="shop__close" aria-label="Закрыть магазин" @click="$emit('close')" type="button">×</button>
      </header>

      <!-- Body grid: left preview / right catalog -->
      <div class="shop__body">
        <!-- LEFT: preview + balance + action -->
        <aside class="shop__left">
          <div class="shop__balance">
            <span class="balance__icon" aria-hidden="true">●</span>
            <div class="balance__info">
              <span class="balance__num">{{ coins }}</span>
              <span class="balance__label">монет</span>
            </div>
          </div>

          <div class="shop__warn">Покупки сохраняются в твоём профиле</div>

          <div class="shop__preview">
            <span class="shop__preview-label">
              {{ activeCategory === 'room' ? (selectedItem?.title || 'Твоя комната') : 'Примерка' }}
            </span>
            <RoomPreview
              v-if="activeCategory === 'room'"
              :equipped-ids="previewRoomIds"
              :selected-title="selectedItem?.title"
            />
            <ShopPreview v-else :appearance="appearance" :equipped="previewEquipped" />
          </div>

          <div class="shop__selected">
            <template v-if="selectedItem">
              <div class="shop__selected-title">
                <ShopItemThumbnail :item-id="selectedItem.id" :title="selectedItem.title" />
                <div class="shop__selected-text">
                  <span class="shop__selected-name">{{ selectedItem.title }}</span>
                  <span class="shop__selected-rarity" :style="{ color: rarityColor(selectedItem.rarity) }">
                    {{ rarityLabel(selectedItem.rarity) }}
                  </span>
                </div>
              </div>
              <p class="shop__selected-desc">{{ selectedItem.description }}</p>
            </template>
            <template v-else>
              <p class="shop__hint">Выбери предмет из каталога</p>
            </template>
          </div>

          <button
            class="shop__action"
            :class="actionClass"
            :disabled="!actionEnabled"
            @click="onPrimaryAction"
            type="button"
          >
            {{ actionLabel }}
          </button>
        </aside>

        <!-- RIGHT: catalog header + grid -->
        <section class="shop__right">
          <div class="shop__right-head">
            <ShopCategoryTabs v-model:value="activeCategory" />
          </div>

          <div v-if="activeCategory === 'room'" class="shop__room-filters" aria-label="Виды предметов для комнаты">
            <button
              v-for="filter in roomFilterOptions"
              :key="filter.key"
              type="button"
              :class="{ 'shop__room-filter--active': roomFilter === filter.key }"
              @click="roomFilter = filter.key"
            >
              {{ filter.label }}
            </button>
          </div>

          <div class="shop__filters">
            <button
              v-for="opt in sortOptions"
              :key="opt.key"
              class="shop__sort-btn"
              :class="{ 'shop__sort-btn--active': sort === opt.key }"
              @click="sort = opt.key"
              type="button"
            >
              {{ opt.label }}
            </button>
          </div>

          <div class="shop__grid-wrap">
            <div v-if="loading" class="shop__msg">Загружаем магазин…</div>
            <div v-else-if="filteredItems.length === 0" class="shop__msg">
              В этой категории пока пусто
            </div>
            <div v-else :key="activeCategory + sort + roomFilter" class="shop__grid">
              <ShopItemCard
                v-for="item in filteredItems"
                :key="item.id"
                :item="item"
                :coins="coins"
                :selected="selectedId === item.id"
                @select="onSelect"
              />
            </div>
          </div>
        </section>
      </div>

      <div v-if="selectedItem" class="shop__mobile-action">
        <ShopItemThumbnail :item-id="selectedItem.id" :title="selectedItem.title" />
        <div class="shop__mobile-selection">
          <strong>{{ selectedItem.title }}</strong>
          <span :style="{ color: rarityColor(selectedItem.rarity) }">{{ rarityLabel(selectedItem.rarity) }}</span>
        </div>
        <button
          class="shop__action"
          :class="actionClass"
          :disabled="!actionEnabled"
          @click="onPrimaryAction"
          type="button"
        >
          {{ actionLabel }}
        </button>
      </div>

      <!-- Toast -->
      <transition name="toast">
        <div v-if="toast" class="shop__toast" :class="'shop__toast--' + toast.type">
          {{ toast.text }}
        </div>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, defineAsyncComponent, onMounted, ref, watch } from "vue"
import ShopCategoryTabs from "./ShopCategoryTabs.vue"
import ShopItemCard from "./ShopItemCard.vue"
import ShopPreview from "./ShopPreview.vue"
import ShopItemThumbnail from "./ShopItemThumbnail.vue"
import type { PlayerAppearance } from "../shared/types"
import type { ShopCategory, ShopItemDto, ShopRarity, ShopSlot } from "../shared/shopTypes"
import { RARITY_COLOR, RARITY_LABEL, conflictingShopSlots } from "../shared/shopTypes"
import { shopListRoute } from "../api/shop/list"
import { shopBuyRoute } from "../api/shop/buy"
import { shopEquipRoute } from "../api/shop/equip"
import { SHOP_CATALOG } from "../shared/shopCatalog"
import { METRIKA_GOALS, reachMetrikaGoal } from "../shared/metrika"

const RoomPreview = defineAsyncComponent(() => import("./RoomPreview.vue"))

const props = defineProps<{
  sessionId: string
  appearance: PlayerAppearance
  initialCoins: number
  initialCategory?: ShopCategory
}>()

const emit = defineEmits<{
  close: []
  coinsChanged: [coins: number]
}>()

const coins = ref(props.initialCoins)
const authorized = ref(false)
const items = ref<ShopItemDto[]>([])
const loading = ref(true)
const busy = ref(false)
const activeCategory = ref<ShopCategory>(props.initialCategory || "hats")
const selectedId = ref<string | null>(null)
type RoomFilterKey = "all" | Extract<ShopSlot, `room_${string}`>
const roomFilter = ref<RoomFilterKey>("all")
const roomFilterOptions: Array<{ key: RoomFilterKey; label: string }> = [
  { key: "all", label: "Все" },
  { key: "room_wallpaper", label: "Обои" },
  { key: "room_bed", label: "Кровати" },
  { key: "room_desk", label: "Столы" },
  { key: "room_desk_chair", label: "Рабочие стулья" },
  { key: "room_computer", label: "Компьютеры" },
  { key: "room_shelf", label: "Полки" },
  { key: "room_cabinet", label: "Шкафы" },
  { key: "room_rug", label: "Ковры" },
  { key: "room_light", label: "Свет" },
  { key: "room_seat", label: "Кресла" },
  { key: "room_wall", label: "Декор стен" },
]

type SortKey = "default" | "cheap" | "expensive" | "new" | "rarity"
const sort = ref<SortKey>("default")
const sortOptions: Array<{ key: SortKey; label: string }> = [
  { key: "default", label: "По умолчанию" },
  { key: "cheap", label: "Сначала дешёвые" },
  { key: "expensive", label: "Сначала дорогие" },
  { key: "new", label: "Новинки" },
  { key: "rarity", label: "По редкости" },
]

const rarityWeight: Record<ShopRarity, number> = {
  legendary: 4,
  epic: 3,
  rare: 2,
  common: 1,
}

const toast = ref<{ text: string; type: "ok" | "err" } | null>(null)
let toastTimer: any = null
function showToast(text: string, type: "ok" | "err") {
  toast.value = { text, type }
  if (toastTimer) clearTimeout(toastTimer)
  toastTimer = setTimeout(() => (toast.value = null), 2400)
}

const equippedItems = computed(() => items.value.filter((it) => it.equipped))
const selectedItem = computed(() =>
  selectedId.value ? items.value.find((it) => it.id === selectedId.value) || null : null,
)

/**
 * Превью показывает реально надетые предметы.
 * Если выбран не надетый и купленный предмет — подменяем слот для preview.
 * Если выбран не купленный — показываем «примерку» (тоже подменяем слот),
 * чтобы пользователь видел, как это будет выглядеть.
 */
const previewEquipped = computed<ShopItemDto[]>(() => {
  const base = equippedItems.value
  const sel = selectedItem.value
  if (!sel) return base
  if (sel.equipped) return base
  const conflicts = new Set(conflictingShopSlots(sel.slot))
  const filtered = base.filter((it) => !conflicts.has(it.slot))
  return [...filtered, sel]
})
const previewRoomIds = computed(() => previewEquipped.value.filter((item) => item.category === "room").map((item) => item.id))

const filteredItems = computed(() => {
  let list = items.value.filter((it) => it.category === activeCategory.value)
  if (activeCategory.value === "room" && roomFilter.value !== "all") {
    list = list.filter((item) => item.slot === roomFilter.value)
  }
  if (sort.value === "cheap") list = [...list].sort((a, b) => a.price - b.price)
  else if (sort.value === "expensive") list = [...list].sort((a, b) => b.price - a.price)
  else if (sort.value === "new")
    list = [...list].sort((a, b) => Number(Boolean(b.isNew)) - Number(Boolean(a.isNew)))
  else if (sort.value === "rarity")
    list = [...list].sort((a, b) => rarityWeight[b.rarity] - rarityWeight[a.rarity])
  return list
})

watch(activeCategory, () => {
  selectedId.value = null
  roomFilter.value = "all"
})

const actionLabel = computed(() => {
  const sel = selectedItem.value
  if (!sel) return "Выбери предмет"
  if (busy.value) return "Подожди…"
  if (sel.equipped) return sel.category === "room" ? "Убрать из комнаты" : "Снять"
  if (sel.owned) return sel.category === "room" ? "Разместить" : "Надеть"
  if (!authorized.value) return "Профиль не найден"
  if (coins.value < sel.price) return `Не хватает ${sel.price - coins.value} монет`
  return `Купить за ${sel.price} монет`
})

const actionClass = computed(() => {
  const sel = selectedItem.value
  if (!sel) return "shop__action--disabled"
  if (sel.equipped) return "shop__action--unequip"
  if (sel.owned) return "shop__action--equip"
  return "shop__action--buy"
})

const actionEnabled = computed(() => {
  const sel = selectedItem.value
  if (!sel || busy.value) return false
  if (sel.equipped) return true
  if (sel.owned) return true
  return authorized.value && coins.value >= sel.price
})

function rarityColor(r: ShopRarity) { return RARITY_COLOR[r] }
function rarityLabel(r: ShopRarity) { return RARITY_LABEL[r] }

function onSelect(item: ShopItemDto) {
  selectedId.value = item.id
}

async function onPrimaryAction() {
  const sel = selectedItem.value
  if (!sel || busy.value) return
  if (sel.owned) {
    await toggleEquip(sel)
  } else {
    await buyItem(sel)
  }
}

async function load() {
  loading.value = true
  try {
    const res = await shopListRoute.query({ sessionId: props.sessionId }).run(ctx)
    const owned = new Set(res.ownedIds)
    const equipped = new Set(res.equippedIds)
    items.value = SHOP_CATALOG.map((item) => ({ ...item, owned: owned.has(item.id), equipped: equipped.has(item.id) }))
    coins.value = res.coins
    authorized.value = res.authorized
    emit("coinsChanged", res.coins)
  } finally {
    loading.value = false
  }
}

async function buyItem(item: ShopItemDto) {
  if (!authorized.value) {
    showToast("Профиль устройства не найден", "err")
    return
  }
  if (coins.value < item.price) {
    showToast("Не хватает монет — соверши фокус-сессию!", "err")
    return
  }
  busy.value = true
  try {
    const res = await shopBuyRoute.run(ctx, { sessionId: props.sessionId, itemId: item.id })
    if (res.success) {
      reachMetrikaGoal(METRIKA_GOALS.itemPurchased, {
        item_id: item.id,
        category: item.category,
        coin_price: item.price,
      })
      coins.value = res.coins
      emit("coinsChanged", res.coins)
      if (item.category === "room") {
        const placed = await shopEquipRoute.run(ctx, { itemId: item.id, sessionId: props.sessionId, equip: true })
        if (!placed.success) throw new Error("Не удалось разместить предмет")
        showToast(`Добавлено в комнату: ${item.title}`, "ok")
        items.value = items.value.map((it) => {
          if (it.id === item.id) return { ...it, owned: true, equipped: true }
          if (it.slot === item.slot) return { ...it, equipped: false }
          return it
        })
      } else {
        showToast(`Куплено: ${item.title}`, "ok")
        items.value = items.value.map((it) => it.id === item.id ? { ...it, owned: true } : it)
      }
    } else {
      showToast(res.error || "Ошибка покупки", "err")
    }
  } catch (e) {
    showToast("Ошибка покупки", "err")
  } finally {
    busy.value = false
  }
}

async function toggleEquip(item: ShopItemDto) {
  busy.value = true
  try {
    const newEquip = !item.equipped
    const res = await shopEquipRoute.run(ctx, {
      itemId: item.id,
      sessionId: props.sessionId,
      equip: newEquip,
    })
    if (res.success) {
      showToast(newEquip ? (item.category === "room" ? `Размещено: ${item.title}` : `Надето: ${item.title}`) : (item.category === "room" ? "Убрано из комнаты" : "Снято"), "ok")
      // Реактивно: снимаем все предметы того же слота, надеваем выбранный
      items.value = items.value.map((it) => {
        if (it.id === item.id) return { ...it, equipped: newEquip }
        if (newEquip && conflictingShopSlots(item.slot).includes(it.slot)) return { ...it, equipped: false }
        return it
      })
    } else {
      showToast(res.error || "Ошибка", "err")
    }
  } catch (e) {
    showToast("Ошибка", "err")
  } finally {
    busy.value = false
  }
}

onMounted(load)
</script>

<style scoped>
.shop {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  top: var(--college-header-h, 56px);
  background: rgba(41, 43, 51, 0.62);
  z-index: 80;
  display: flex;
  align-items: stretch;
  justify-content: center;
  padding: 0;
  backdrop-filter: blur(8px);
  font-family: "Inter", system-ui, -apple-system, sans-serif;
}

@media (max-width: 767px) {
  .shop {
    top: 0;
    bottom: calc(72px + env(safe-area-inset-bottom));
    z-index: 300;
  }
}

.shop__panel {
  width: 100%;
  max-width: 1180px;
  background: #FFFDF8;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  box-shadow: 0 30px 80px rgba(41, 43, 51, 0.2);
}

.shop__mobile-balance {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  min-height: 36px;
  padding: 0 11px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.16);
  font-size: 13px;
  white-space: nowrap;
}
.shop__mobile-balance span { color: #F2B84B; font-size: 15px; }
.shop__mobile-balance strong { font-size: 13px; }

@media (min-width: 768px) {
  .shop {
    padding: 24px;
    align-items: center;
  }
  .shop__panel {
    border-radius: 24px;
    max-height: calc(100vh - var(--college-header-h, 56px) - 48px);
  }
  .shop__mobile-balance { display: none; }
}

/* HEADER */
.shop__head {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: max(8px, env(safe-area-inset-top)) 12px 8px;
  background: #6258D9;
  color: #fff;
  flex-shrink: 0;
}
.shop__title-wrap { flex: 1; }
.shop__title {
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.3px;
}
.shop__subtitle {
  display: none;
}
.shop__close {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.2);
  color: #fff;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  flex-shrink: 0;
  transition: background 0.15s;
}
.shop__close:hover { background: rgba(255, 255, 255, 0.32); }

@media (min-width: 768px) {
  .shop__head { padding: 16px 22px; }
  .shop__title { font-size: 22px; }
  .shop__subtitle { display: block; margin: 2px 0 0; font-size: 12px; font-weight: 600; opacity: 0.9; }
  .shop__close { width: 44px; height: 44px; }
}

/* BODY — единый скролл-контейнер */
.shop__body {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.shop__body::-webkit-scrollbar {
  width: 10px;
}
.shop__body::-webkit-scrollbar-track {
  background: #ECE8DE;
}
.shop__body::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, #7A70E2, #6258D9);
  border-radius: 999px;
  border: 2px solid #ECE8DE;
}
.shop__body {
  scrollbar-width: thin;
  scrollbar-color: #6258D9 #ECE8DE;
}

@media (min-width: 900px) {
  .shop__body {
    display: grid;
    grid-template-columns: minmax(280px, 30%) 1fr;
    align-items: start;
  }
}

/* LEFT */
.shop__left {
  display: grid;
  grid-template-columns: 150px 1fr;
  grid-template-areas:
    "preview balance"
    "preview warn"
    "preview selected"
    "action  action";
  column-gap: 12px;
  row-gap: 8px;
  padding: 12px 14px;
  background: #fff;
  border-bottom: 1px solid #ECE8DE;
}

.shop__left .shop__balance { grid-area: balance; }
.shop__left .shop__warn { grid-area: warn; }
.shop__left .shop__preview { grid-area: preview; }
.shop__left .shop__selected { grid-area: selected; }
.shop__left .shop__action { grid-area: action; }

/* На мобилке скрываем слот-индикаторы — на десктопе будут видны */
.shop__left :deep(.preview__slots) {
  display: none;
}

@media (min-width: 900px) {
  .shop__left {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 18px 18px 18px 22px;
    border-bottom: none;
    border-right: 1px solid #ECE8DE;
    position: sticky;
    top: 0;
    align-self: start;
    max-height: calc(100vh - 48px - 70px);
    overflow-y: auto;
  }
  .shop__left :deep(.preview__slots) {
    display: grid;
  }
}
.shop__balance {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 12px;
  background: rgba(242, 184, 75, 0.18);
  border: 1px solid rgba(242, 184, 75, 0.38);
}
.balance__icon { font-size: 22px; }
.balance__icon { color: #f2b84b; }
.balance__info { display: flex; flex-direction: column; line-height: 1; }
.balance__num { font-size: 18px; font-weight: 700; color: #292B33; }
.balance__label {
  margin-top: 2px;
  font-size: 9px;
  font-weight: 700;
  color: #666873;
  letter-spacing: 0.5px;
  text-transform: none;
}
@media (min-width: 900px) {
  .shop__balance {
    gap: 10px;
    padding: 12px 14px;
    border-radius: 14px;
  }
  .balance__icon { font-size: 28px; }
  .balance__num { font-size: 22px; }
  .balance__label { font-size: 10px; letter-spacing: 0.6px; margin-top: 3px; }
}

.shop__warn {
  font-size: 12px;
  font-weight: 700;
  color: #6258D9;
  background: #F0EEF9;
  padding: 8px 12px;
  border-radius: 10px;
  text-align: center;
}

.shop__preview {
  position: relative;
  background: #F6F2E9;
  border-radius: 16px;
  padding: 8px;
}
.shop__preview-label {
  display: none;
}
.room-preview {
  position: relative;
  min-height: 210px;
  overflow: hidden;
  border-radius: 14px;
  background: linear-gradient(180deg, #eee7da 0 62%, #d9c8ac 62% 100%);
}
.room-preview__window { position: absolute; width: 68px; height: 58px; left: 50%; top: 18px; translate: -50% 0; border: 7px solid #4a4c55; background: #89b8e8; }
.room-preview__window::after { content: ""; position: absolute; left: 27px; top: 0; width: 6px; height: 100%; background: #4a4c55; }
.room-preview__desk { position: absolute; width: 108px; height: 16px; left: 50%; top: 105px; translate: -50% 0; border-radius: 6px; background: #765d49; box-shadow: -38px 38px 0 -5px #393b44, 38px 38px 0 -5px #393b44; }
.room-preview__shelf { position: absolute; right: 16px; top: 64px; width: 62px; height: 8px; border-radius: 4px; background: #5a493b; }
.room-preview__shelf i { display: inline-block; width: 10px; height: 18px; margin: -16px 3px 0; border-radius: 5px 5px 2px 2px; background: #87a66a; }
.room-preview__rug { position: absolute; left: 50%; bottom: 12px; width: 124px; height: 52px; translate: -50% 0; border-radius: 50%; background: rgba(135,166,106,.74); }
.room-preview__lamp { position: absolute; right: 22px; bottom: 34px; width: 7px; height: 72px; background: #51443a; }
.room-preview__lamp::before { content: ""; position: absolute; left: -15px; top: -5px; width: 37px; height: 27px; border-radius: 18px 18px 7px 7px; background: #f2b84b; }
.room-preview__chair { position: absolute; right: 52px; bottom: 20px; width: 45px; height: 42px; border-radius: 16px 16px 10px 10px; background: #89b8e8; box-shadow: 0 -23px 0 -5px #7aa8d3; }
.room-preview__print { position: absolute; left: 18px; top: 28px; width: 55px; height: 38px; border: 5px solid #51443a; background: linear-gradient(165deg, #f6f2e9 45%, #6258d9 46% 56%, #f6f2e9 57%); }
.room-preview__rug--stage { background: radial-gradient(circle, #f2b84b 0 12%, #8d76e8 13% 52%, #6258d9 53% 100%); box-shadow: inset 0 0 0 3px rgba(255,255,255,.35); }
.room-preview__lamp--lilac::before { background: #8d76e8; box-shadow: 0 8px 28px rgba(141,118,232,.65); }
.room-preview__chair--studio { background: #45424f; box-shadow: 0 -23px 0 -5px #8d76e8; }
.room-preview__photocards { position: absolute; left: 18px; top: 24px; width: 62px; display: grid; grid-template-columns: repeat(3, 1fr); gap: 3px; padding: 5px; border: 4px solid #51443a; background: #fffdf8; }
.room-preview__photocards i { height: 19px; border-radius: 2px; background: linear-gradient(145deg, #d7d1ec 0 52%, #8d76e8 53%); }

.shop__selected {
  background: #FFFDF8;
  border: 1px dashed #D7D3CA;
  border-radius: 14px;
  padding: 10px 12px;
  min-height: 60px;
}
.shop__selected-title {
  display: flex;
  align-items: center;
  gap: 10px;
}
.shop__selected-title :deep(.item-thumbnail) { width: 56px; height: 56px; border-radius: 14px; flex: 0 0 56px; }
.shop__selected-text { display: flex; flex-direction: column; line-height: 1.2; }
.shop__selected-name {
  font-size: 14px;
  font-weight: 700;
  color: #292B33;
}
.shop__selected-rarity {
  margin-top: 2px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.6px;
  text-transform: none;
}
.shop__selected-desc {
  margin: 6px 0 0;
  font-size: 12px;
  color: #666873;
  font-weight: 600;
  line-height: 1.4;
}
.shop__hint {
  margin: 0;
  text-align: center;
  font-size: 12px;
  color: #7C7E88;
  font-weight: 700;
}

.shop__action {
  margin-top: auto;
  padding: 14px;
  border-radius: 14px;
  border: none;
  font-weight: 700;
  font-size: 14px;
  cursor: pointer;
  font-family: "Inter", system-ui, sans-serif;
  letter-spacing: 0.4px;
  text-transform: none;
  transition: transform 0.15s, opacity 0.15s, box-shadow 0.15s;
  color: #fff;
}
.shop__action:not(:disabled):active { transform: scale(0.98); }
.shop__action:disabled { opacity: 0.5; cursor: not-allowed; }
.shop__action--buy {
  background: #6258D9;
  box-shadow: 0 8px 18px rgba(98, 88, 217, 0.24);
}
.shop__action--equip {
  background: #87A66A;
  box-shadow: 0 8px 18px rgba(135, 166, 106, 0.24);
}
.shop__action--unequip {
  background: #F6F2E9;
  color: #666873;
}
.shop__action--disabled {
  background: #DFDCD4;
  color: #7C7E88;
}

/* RIGHT — без своего скролла, всё внутри общего .shop__body */
.shop__right {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.shop__right-head {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px 8px;
  background: #FFFDF8;
  border-bottom: 1px solid #ECE8DE;
}

.shop__room-filters {
  display: flex;
  gap: 6px;
  overflow-x: auto;
  padding: 10px 12px;
  border-bottom: 1px solid #ECE8DE;
  background: #F8F5EE;
  scrollbar-width: none;
}
.shop__room-filters::-webkit-scrollbar { display: none; }
.shop__room-filters button {
  min-height: 38px;
  flex: 0 0 auto;
  padding: 7px 11px;
  border: 1px solid #DDD8CE;
  border-radius: 999px;
  background: #FFFDF8;
  color: #666873;
  font: 750 11px/1.1 "Inter", system-ui, sans-serif;
  cursor: pointer;
  white-space: nowrap;
}
.shop__room-filters button:hover { border-color: #B9B2E7; color: #6258D9; }
.shop__room-filters .shop__room-filter--active {
  border-color: #6258D9;
  background: #EEEAF8;
  color: #5147C4;
}

.shop__filters {
  display: flex;
  gap: 6px;
  flex-wrap: nowrap;
  overflow-x: auto;
  padding: 8px 12px 10px;
  background: #FFFDF8;
  border-bottom: 1px solid #ECE8DE;
}
.shop__filters::-webkit-scrollbar { display: none; }
.shop__sort-btn {
  padding: 5px 10px;
  border-radius: 8px;
  border: none;
  background: #fff;
  color: #666873;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
  font-family: "Inter", system-ui, sans-serif;
  text-transform: none;
  letter-spacing: 0.4px;
  border: 1px solid #ECE8DE;
  transition: all 0.15s;
  flex: 0 0 auto;
  min-height: 36px;
}
.shop__sort-btn:hover { color: #6258D9; border-color: #DCD8F5; }
.shop__sort-btn--active {
  background: #6258D9;
  color: #fff;
  border-color: #6258D9;
}

.shop__grid-wrap {
  padding: 12px 12px calc(24px + env(safe-area-inset-bottom));
}

/* Mobile: stable two-column catalog */
.shop__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}
.shop__grid > * {
  min-width: 0;
}

/* Desktop: вертикальная сетка, без отдельного скролла — листается с .shop__body */
@media (min-width: 900px) {
  .shop__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
    gap: 12px;
    overflow: visible;
    scroll-snap-type: none;
    padding-bottom: 0;
  }
  .shop__grid > * {
    flex: initial;
  }
}

.shop__mobile-action {
  display: grid;
  grid-template-columns: 44px minmax(0, 1fr) minmax(118px, auto);
  align-items: center;
  gap: 9px;
  padding: 9px 12px;
  border-top: 1px solid #E4E0D7;
  background: rgba(255, 253, 248, 0.97);
  box-shadow: 0 -10px 28px rgba(41, 43, 51, 0.12);
  flex-shrink: 0;
}
.shop__mobile-action :deep(.item-thumbnail) {
  width: 44px;
  height: 44px;
  border-radius: 12px;
}
.shop__mobile-selection {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 2px;
}
.shop__mobile-selection strong {
  overflow: hidden;
  color: #292B33;
  font-size: 12px;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.shop__mobile-selection span { font-size: 10px; font-weight: 700; }
.shop__mobile-action .shop__action {
  margin: 0;
  min-height: 44px;
  padding: 8px 12px;
  font-size: 11px;
  line-height: 1.15;
}

@media (min-width: 900px) {
  .shop__mobile-action { display: none; }
  .shop__right-head { padding: 14px 18px 6px; }
  .shop__filters { flex-wrap: wrap; overflow: visible; padding: 10px 18px; }
  .shop__grid-wrap { padding: 14px 18px 22px; }
}

.shop__msg {
  text-align: center;
  padding: 40px 16px;
  color: #666873;
  font-weight: 700;
}

/* TOAST */
.shop__toast {
  position: absolute;
  bottom: 22px;
  left: 50%;
  transform: translateX(-50%);
  padding: 10px 18px;
  border-radius: 999px;
  font-weight: 700;
  font-size: 13px;
  box-shadow: 0 12px 30px rgba(0,0,0,0.2);
  z-index: 10;
}
.shop__toast--ok { background: #87A66A; color: #fff; }
.shop__toast--err { background: #292B33; color: #fff; }
.toast-enter-from, .toast-leave-to {
  opacity: 0;
  transform: translate(-50%, 12px);
}
.toast-enter-active, .toast-leave-active {
  transition: opacity 0.2s, transform 0.2s;
}

@media (max-width: 899px) {
  .shop__left {
    display: block;
    position: sticky;
    top: 0;
    z-index: 4;
    padding: 8px 12px;
    border-bottom: 1px solid #ECE8DE;
    background: #FFFDF8;
    box-shadow: 0 8px 18px rgba(41, 43, 51, 0.09);
  }
  .shop__left > :not(.shop__preview) { display: none; }
  .shop__preview { padding: 0; border: 1px solid #E4E0D7; overflow: hidden; }
  .shop__preview-label {
    position: absolute;
    top: 8px;
    left: 8px;
    z-index: 2;
    display: inline-flex;
    align-items: center;
    min-height: 26px;
    padding: 0 10px;
    border: 1px solid rgba(98, 88, 217, 0.18);
    border-radius: 999px;
    background: rgba(255, 253, 248, 0.92);
    color: #6258D9;
    box-shadow: 0 4px 12px rgba(41, 43, 51, 0.08);
    font-size: 11px;
    font-weight: 700;
    pointer-events: none;
  }
  .shop__preview :deep(.preview) { gap: 0; }
  .shop__preview :deep(.preview__slots) { display: none; }
  .shop__preview :deep(.preview__stage),
  .shop__preview :deep(.preview__stage > .preview) { height: 182px; min-height: 182px; border-radius: 14px; }
  .room-preview { min-height: 182px; }
  .shop__grid-wrap { padding-bottom: 18px; }
}
@media (max-width: 767px) {
  .shop { bottom: calc(72px + env(safe-area-inset-bottom)); }
}
</style>
